import {
  Component,
  DestroyRef,
  computed,
  effect,
  inject,
  input,
  signal,
  untracked,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import { HttpErrorResponse } from '@angular/common/http';
import { CardComponent } from '@custom-components/general/card/card.component';
import { CardGridComponent } from '@custom-components/general/card-grid';
import { TableComponent } from '@custom-components/general/table/table.component';
import { FilledButtonComponent, TextButtonComponent } from '@custom-components/general/button';
import { SelectFieldComponent } from '@custom-components/general/form/select-field/select-field.component';
import { DetailFieldComponent } from '@custom-components/general/detail-field';
import { TableColumnConfig, TableColumnType } from '@core/models/skeleton.model';
import {
  DeviceModel,
  ManualPhaseCount,
  MeterRole,
  METER_ROLE_ICONS,
  PhaseCount,
  StaticStatus,
  effectiveMeterRole,
  getUserInitialPartner,
} from '@core/models';
import { AlarmConfigService } from '@core/services/alarm-config.service';
import { DeviceService } from '@core/services/device.service';
import { StatusService } from '@core/services/status.service';
import { DeviceStore } from '@root/app/store/device.store';
import { UserStore } from '@root/app/store/user.store';
import { XEMEX_OID } from '@core/constants';
import { TagStateOf, alarmConfigGroupLabelKey } from '@core/helpers';
import { capitalizePriority } from '@core/models';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Observable, catchError, forkJoin, map, of } from 'rxjs';
import { PvForecastCardComponent } from '../pv-forecast-card/pv-forecast-card.component';
import { ConfirmResetDeviceDialogComponent } from '../confirm-reset-device-dialog/confirm-reset-device-dialog.component';

const PHASE_COUNT_OPTIONS: ManualPhaseCount[] = ['unknown', '1', '3'];
const METER_ROLE_OPTIONS: MeterRole[] = ['consumption', 'production', 'bidirectional'];

/** The one `dm.device_type.protocol` value whose devices have a DLMS event log of their own.
 * Lewiz (`EnnyLCU`) and Smart Connect (`MQTT`) can never raise a `dlms.*` alarm, so those config
 * groups are left out of this device's table entirely instead of offered as settings that could
 * never take effect. Compared lowercased: the casing in the database is not consistent — the
 * CHECK constraint on the column spells the values `DLMS`/`EnnyLCU`/`MQTT` but SmartConnect is
 * stored as `mqtt`, and SQL Server's case-insensitive collation accepts either. */
const DLMS_PROTOCOL = 'dlms';

const DLMS_CONFIG_GROUP_PREFIX = 'dlms.';

interface AlarmSettingsRow extends Record<string, unknown> {
  configGroup: string;
  label: string;
  /** Read-only here — priority is only ever configured account-wide (AlarmsSettingsTabComponent),
   * shown on this tab purely as a colored accent so a device viewer knows an alarm type's
   * severity without being able to change it from a single device. */
  priority: 'High' | 'Medium' | 'Low';
  priorityState: ReturnType<typeof TagStateOf.alarmPriority>;
  showInPortal: boolean;
  emailNotification: boolean;
  /** This device's own effective global values, resolved backend-side — a device override is
   * only ever sent to the backend as an explicit value when it actually diverges from these. */
  globalShowInPortal: boolean;
  globalEmailNotification: boolean;
  /** Straight from the API's `configurable` — false for config groups whose settings are
   * Xemex-fixed (the sim.* groups), which the backend rejects device-override writes for with a
   * 403. Suppresses the per-row toggle interactivity below. */
  configurable: boolean;
}

@Component({
  selector: 'xemex-device-settings-tab',
  imports: [
    MatButtonModule,
    MatIconModule,
    MatSelectModule,
    CardComponent,
    CardGridComponent,
    TableComponent,
    FilledButtonComponent,
    TextButtonComponent,
    SelectFieldComponent,
    DetailFieldComponent,
    TranslateModule,
    PvForecastCardComponent,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './device-settings-tab.component.html',
})
export class DeviceSettingsTabComponent {
  deviceDetail = input<DeviceModel>();
  partnerDefaultMeterRole = input<MeterRole>();
  canManageDeviceSettings = input(false);
  canEditPhaseCount = input(false);
  canOverridePhaseCountLock = input(false);
  canEditMeterRole = input(false);
  /** The owner-only phase-count read answered 403. Meter role and phase count are then shown as
   * plain values: device-func would refuse every save. */
  meterConfigManagedByOwner = input(false);

  private readonly alarmConfigService = inject(AlarmConfigService);
  private readonly deviceService = inject(DeviceService);
  private readonly statusService = inject(StatusService);
  private readonly deviceStore = inject(DeviceStore);
  private readonly userStore = inject(UserStore);
  private readonly destroyRef = inject(DestroyRef);
  private readonly snackBar = inject(MatSnackBar);
  private readonly translateService = inject(TranslateService);
  private readonly dialog = inject(MatDialog);

  protected readonly phaseCountOptions = PHASE_COUNT_OPTIONS;
  /** Draft state for the phase-count select, same convention as `alarmRows`/`savedAlarmRows`
   * below — nothing reaches the backend until `saveAll()` runs. Always one of the 3
   * user-selectable values, even when the device's real state is 'unknown_exhausted' - see
   * rawPhaseCount/phaseCountExhausted for that raw state. */
  protected readonly phaseCountDraft = signal<ManualPhaseCount>('unknown');
  private readonly savedPhaseCount = signal<ManualPhaseCount | undefined>(undefined);

  /** The device's actual backend value, including 'unknown_exhausted' - kept separate from
   * the select's draft/saved state (always a ManualPhaseCount) so the select never has to
   * represent a value that isn't one of its own options. */
  private readonly rawPhaseCount = signal<PhaseCount | null | undefined>(undefined);
  protected readonly phaseCountExhausted = computed(
    () => this.rawPhaseCount() === 'unknown_exhausted',
  );
  /** No detection attempt has been made yet - distinct from 'unknown' (detection ran and was
   * inconclusive) and from 'unknown_exhausted' (it gave up after five attempts). */
  protected readonly phaseCountNotDetected = computed(() => this.rawPhaseCount() === null);

  protected readonly hasUnsavedPhaseCountChange = computed(
    () => this.savedPhaseCount() !== undefined && this.phaseCountDraft() !== this.savedPhaseCount(),
  );

  /** Two independent reasons to disable: the role can't edit at all, or the device type fixes
   * the phase count backend-side (a save would 400, see buildPhaseCountSaveRequest below).
   * That second lock now comes straight from the backend (`phaseCountFixed` on the phase-count
   * read) rather than from matching deviceType text, so the grey-out can't drift from the
   * condition the backend actually enforces. Partner Admin and Partner User
   * (canOverridePhaseCountLock) stay exempt from it — every other editor role (Mechanic,
   * Xemex staff) respects it. */
  protected readonly phaseCountSelectDisabled = computed(
    () =>
      !this.canEditPhaseCount() ||
      (this.deviceDetail()?.phaseCountFixed === true && !this.canOverridePhaseCountLock()),
  );

  protected readonly meterRoleOptions = METER_ROLE_OPTIONS;
  protected readonly meterRoleIcons = METER_ROLE_ICONS;
  /** Draft state for the meter-role select, same convention as `alarmRows`/`savedAlarmRows`
   * below — nothing reaches the backend until `saveAll()` runs. */
  protected readonly meterRoleDraft = signal<MeterRole>('bidirectional');
  private readonly savedMeterRole = signal<MeterRole | undefined>(undefined);

  protected readonly hasUnsavedMeterRoleChange = computed(
    () => this.savedMeterRole() !== undefined && this.meterRoleDraft() !== this.savedMeterRole(),
  );

  /** Draft state, edited locally by the toggles — nothing reaches the backend until
   * `saveAll()` runs, same pattern as AlarmsSettingsTabComponent. */
  protected readonly alarmRows = signal<AlarmSettingsRow[]>([]);
  /** Last-known-persisted state, used to detect unsaved changes and to revert on discard. */
  private readonly savedAlarmRows = signal<AlarmSettingsRow[]>([]);

  /** GET /alarm-config/device-overrides/{id} returns every config group regardless of what the
   * device can actually raise, so the DLMS groups are filtered out here rather than backend-side.
   * An empty/unknown protocol keeps them, so the table doesn't drop rows while the device detail
   * is still loading — same reasoning as the general tab's network card. */
  private readonly supportsDlmsAlarms = computed(() => {
    const protocol = this.deviceDetail()?.protocol?.trim().toLowerCase();
    return !protocol || protocol === DLMS_PROTOCOL;
  });

  /** Filtered for display only — `alarmRows`/`savedAlarmRows` keep every group, so a hidden row
   * stays identical to its saved counterpart and can never turn up as an unsaved change or in a
   * save request. */
  protected readonly visibleAlarmRows = computed(() =>
    this.supportsDlmsAlarms()
      ? this.alarmRows()
      : this.alarmRows().filter(row => !row.configGroup.startsWith(DLMS_CONFIG_GROUP_PREFIX)),
  );

  protected readonly resettingPhaseCountDetection = signal(false);

  protected readonly saving = signal(false);
  protected readonly initialLoading = signal(false);
  protected readonly resetInProgress = signal(false);
  /** True when GET /alarm-config/device-overrides itself failed — every device has alarm
   * settings, so a genuinely empty response never happens; distinguish failure from empty so
   * the tab says why it's blank instead of silently looking like "no config exists". */
  protected readonly loadError = signal(false);
  /** GET /alarm-config/device-overrides answered 403: the user lacks manage_device_settings for
   * this device, so this is not a failure to report. */
  protected readonly alarmSettingsForbidden = signal(false);

  private readonly isXemex = computed(
    () => getUserInitialPartner(this.userStore.user())?.partnerOid === XEMEX_OID,
  );
  /** The reset checks manage_device_settings on the device like the alarm settings read, so a 403
   * there means the reset would be refused too. Xemex users are the exception: the read refuses
   * them outright while the reset lets them through. */
  protected readonly showResetSection = computed(
    () => this.canManageDeviceSettings() && (!this.alarmSettingsForbidden() || this.isXemex()),
  );

  protected readonly hasUnsavedChanges = computed(() => {
    const saved = this.savedAlarmRows();
    return this.alarmRows().some((row, i) => {
      const savedRow = saved[i];
      return (
        !savedRow ||
        savedRow.showInPortal !== row.showInPortal ||
        savedRow.emailNotification !== row.emailNotification
      );
    });
  });

  /** One unified unsaved-changes popup covers all three drafts above — see saveAll()/discardAll(). */
  protected readonly hasAnyUnsavedChanges = computed(
    () =>
      this.hasUnsavedChanges() ||
      this.hasUnsavedMeterRoleChange() ||
      this.hasUnsavedPhaseCountChange(),
  );

  protected readonly columns: TableColumnConfig[] = [
    {
      field: 'label',
      label: 'alarmsPage.settings.alarmTypes.columns.type',
      type: TableColumnType.Text,
      bold: true,
      width: '30%',
    },
    {
      field: 'priority',
      label: 'alarmsPage.settings.alarmTypes.columns.priority',
      type: TableColumnType.Tag,
      stateField: 'priorityState',
      width: '17.5%',
    },
    {
      field: 'showInPortal',
      label: 'alarmsPage.settings.alarmTypes.columns.showInPortal',
      type: TableColumnType.Toggle,
      width: '26.25%',
      disabledWhen: row => row['configurable'] === false,
    },
    {
      field: 'emailNotification',
      label: 'alarmsPage.settings.alarmTypes.columns.email',
      type: TableColumnType.Toggle,
      width: '26.25%',
      disabledWhen: row => row['configurable'] === false,
    },
  ];

  private lastLoadedDeviceId: string | undefined;
  private phaseCountSeededForDeviceId: string | undefined;
  private meterRoleSeededForDeviceId: string | undefined;

  constructor() {
    effect(() => {
      const status = this.deviceDetail()?.internalDeviceStatus;
      if (!status) return;

      const deviceId = String((status.item as StaticStatus).device_id);
      if (this.lastLoadedDeviceId === deviceId) return;

      this.lastLoadedDeviceId = deviceId;
      this.loadAlarmSettings(deviceId);
    });

    // Seeds the phase-count draft once per device. GET /devices/{id} resolves first without
    // phaseCount (it's merged in afterwards from the dedicated GET .../phase-count call, see
    // DeviceStore) — gating on deviceId alone seeded the draft from the initial 'unknown'
    // emission and then never re-seeded once the real value arrived, since the deviceId guard
    // was already tripped. Wait for phaseCount to actually be present before marking this
    // device as seeded, so the delayed real value still lands in the draft.
    effect(() => {
      const detail = this.deviceDetail();
      if (
        !detail ||
        detail.phaseCount === undefined ||
        this.phaseCountSeededForDeviceId === detail.deviceId
      ) {
        return;
      }

      this.phaseCountSeededForDeviceId = detail.deviceId;
      this.rawPhaseCount.set(detail.phaseCount);
      // Neither 'unknown_exhausted' nor an empty phase count is a select option - seed the
      // draft as 'unknown' so picking "Automatisch detecteren" (an actual no-op for the
      // select's own value) still sends a real PATCH and re-arms detection, per
      // hasUnsavedPhaseCountChange below. rawPhaseCount keeps the distinction for the hints.
      const selectablePhaseCount: ManualPhaseCount =
        detail.phaseCount === 'unknown_exhausted' || detail.phaseCount === null
          ? 'unknown'
          : detail.phaseCount;
      this.savedPhaseCount.set(selectablePhaseCount);
      this.phaseCountDraft.set(selectablePhaseCount);
    });

    // Seeds the meter-role draft per device, with the same deviceId gating as the alarm-settings
    // load above: re-seeding on every deviceDetail() emission would clobber an in-progress edit.
    // The partner's default meter role arrives after the device detail, so a device that stores
    // no role is first seeded from the 'bidirectional' fallback. It is seeded again when the
    // effective role changes for the same device, unless the user has already edited the draft.
    effect(() => {
      const detail = this.deviceDetail();
      if (!detail) return;

      const meterRole = effectiveMeterRole(detail.meterRole, this.partnerDefaultMeterRole());
      if (
        this.meterRoleSeededForDeviceId === detail.deviceId &&
        (meterRole === untracked(this.savedMeterRole) || untracked(this.hasUnsavedMeterRoleChange))
      ) {
        return;
      }

      this.meterRoleSeededForDeviceId = detail.deviceId;
      this.savedMeterRole.set(meterRole);
      this.meterRoleDraft.set(meterRole);
    });
  }

  /** Single request for every config group's settings for this one device — see
   * AlarmConfigService.getDeviceAlarmSettings (backed by the dedicated
   * GET /alarm-config/device-overrides/{device_id} endpoint), not one call per config group
   * against the full per-partner device list like DeviceOverridesDialogComponent needs to. */
  private loadAlarmSettings(deviceId: string): void {
    this.initialLoading.set(true);
    this.loadError.set(false);
    this.alarmSettingsForbidden.set(false);
    this.alarmConfigService
      .getDeviceAlarmSettings(Number(deviceId))
      .pipe(
        catchError((error: unknown) => {
          if (error instanceof HttpErrorResponse && error.status === 403) {
            this.alarmSettingsForbidden.set(true);
          }
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(collection => {
        this.initialLoading.set(false);
        if (!collection) {
          this.loadError.set(!this.alarmSettingsForbidden());
          return;
        }

        const rows: AlarmSettingsRow[] = collection.items.map(item => {
          const priority = capitalizePriority(item.priority);
          return {
            configGroup: item.config_group,
            label: this.translateService.instant(alarmConfigGroupLabelKey(item.config_group)),
            priority,
            priorityState: TagStateOf.alarmPriority(priority),
            showInPortal: item.show_in_portal,
            emailNotification: item.email_enabled,
            globalShowInPortal: item.global_show_in_portal,
            globalEmailNotification: item.global_email_enabled,
            configurable: item.configurable,
          };
        });
        this.alarmRows.set(rows);
        this.savedAlarmRows.set(rows);
      });
  }

  /** Local-only — nothing is persisted until `saveAll()` runs. */
  protected onToggleChange({ row, field }: { row: AlarmSettingsRow; field: string }): void {
    if (field !== 'showInPortal' && field !== 'emailNotification') return;
    this.alarmRows.update(rows =>
      rows.map(r => (r.configGroup === row.configGroup ? { ...r, [field]: !r[field] } : r)),
    );
  }

  protected onPhaseCountChange(phaseCount: ManualPhaseCount): void {
    this.phaseCountDraft.set(phaseCount);
  }

  /** Separate from saveAll()/the select above: the select already shows "Automatisch
   * detecteren" as selected for an exhausted device (see phaseCountDraft seeding), so
   * re-picking the same option wouldn't fire selectionChange - this button always issues
   * the PATCH regardless of the select's current value. */
  protected onResetPhaseCountDetection(): void {
    const status = this.deviceDetail()?.internalDeviceStatus;
    const deviceId = status ? String((status.item as StaticStatus).device_id) : undefined;
    if (!deviceId) return;

    this.resettingPhaseCountDetection.set(true);
    this.deviceService
      .updatePhaseCount(deviceId, 'unknown')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.resettingPhaseCountDetection.set(false);
          this.rawPhaseCount.set('unknown');
          this.savedPhaseCount.set('unknown');
          this.phaseCountDraft.set('unknown');
          this.deviceStore.setDevicePhaseCount('unknown');
        },
        error: () => {
          this.resettingPhaseCountDetection.set(false);
          this.snackBar.open(
            this.translateService.instant('devices.alarms.settings.saveError'),
            '',
            { horizontalPosition: 'right', verticalPosition: 'top', duration: 3000 },
          );
        },
      });
  }

  protected onMeterRoleChange(meterRole: MeterRole): void {
    this.meterRoleDraft.set(meterRole);
  }

  /** Resets every draft back to its last-saved snapshot — the single unsaved-changes popup
   * discards whatever combination of the three sections is currently dirty. */
  protected discardAll(): void {
    this.alarmRows.set(this.savedAlarmRows());
    const savedRole = this.savedMeterRole();
    if (savedRole) this.meterRoleDraft.set(savedRole);
    const savedPhase = this.savedPhaseCount();
    if (savedPhase) this.phaseCountDraft.set(savedPhase);
  }

  /** Only the sections that actually changed issue a request, and each request still only
   * carries its own changed fields (alarm rows: per-row diff against the global default;
   * meter role/phase count: the single new value) — this fires whichever of the three are dirty
   * together, behind one popup, instead of three separate save actions. */
  protected saveAll(): void {
    const status = this.deviceDetail()?.internalDeviceStatus;
    const deviceId = status ? String((status.item as StaticStatus).device_id) : undefined;
    if (!deviceId) return;

    const requests: Observable<{ ok: boolean; detail?: string }>[] = [];
    const alarmRequest = this.buildAlarmSaveRequest(Number(deviceId));
    if (alarmRequest) requests.push(alarmRequest);
    const meterRoleRequest = this.buildMeterRoleSaveRequest(deviceId);
    if (meterRoleRequest) requests.push(meterRoleRequest);
    const phaseCountRequest = this.buildPhaseCountSaveRequest(deviceId);
    if (phaseCountRequest) requests.push(phaseCountRequest);

    if (!requests.length) return;

    this.saving.set(true);
    forkJoin(requests)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(results => {
        this.saving.set(false);
        const failures = results.filter(r => !r.ok);
        if (failures.length) {
          console.error('Device settings save failed', failures);
          // The phase-count backend has no GET-time "is this device type hardcoded" signal
          // (see phaseCountSelectDisabled above) - a 400 with a `detail` message is how that
          // surfaces, so show it verbatim over the generic error when present.
          const detail = failures.find(f => f.detail)?.detail;
          this.snackBar.open(
            detail ?? this.translateService.instant('devices.alarms.settings.saveError'),
            '',
            { horizontalPosition: 'right', verticalPosition: 'top', duration: 3000 },
          );
          return;
        }
        this.snackBar.open(
          this.translateService.instant('devices.alarms.settings.saveSuccess'),
          '',
          { horizontalPosition: 'right', verticalPosition: 'top', duration: 3000 },
        );
      });
  }

  /** null when the alarm rows aren't dirty — saveAll() only includes this request when there's
   * actually something to send. */
  private buildAlarmSaveRequest(deviceId: number): Observable<{ ok: boolean }> | null {
    const saved = this.savedAlarmRows();
    const changedRows = this.alarmRows().filter((row, i) => {
      const savedRow = saved[i];
      return (
        !savedRow ||
        savedRow.showInPortal !== row.showInPortal ||
        savedRow.emailNotification !== row.emailNotification
      );
    });
    if (!changedRows.length) return null;

    return forkJoin(
      changedRows.map(row =>
        this.alarmConfigService
          .updateDeviceOverride(row.configGroup, deviceId, {
            // null means "inherit the global setting" — only send an explicit value when it
            // actually diverges, same convention as DeviceOverridesDialogComponent.
            show_in_portal: row.showInPortal !== row.globalShowInPortal ? row.showInPortal : null,
            email_enabled:
              row.emailNotification !== row.globalEmailNotification ? row.emailNotification : null,
          })
          .pipe(
            map(() => true),
            catchError(() => of(false)),
          ),
      ),
    ).pipe(
      map(results => {
        const ok = results.every(Boolean);
        if (ok) this.savedAlarmRows.set(this.alarmRows());
        return { ok };
      }),
    );
  }

  /** null when the meter-role draft isn't dirty — see buildAlarmSaveRequest(). */
  private buildMeterRoleSaveRequest(deviceId: string): Observable<{ ok: boolean }> | null {
    if (!this.hasUnsavedMeterRoleChange()) return null;

    const meterRole = this.meterRoleDraft();
    return this.deviceService.updateMeterRole(deviceId, meterRole).pipe(
      map(() => {
        this.savedMeterRole.set(meterRole);
        this.deviceStore.setDeviceMeterRole(meterRole);
        return { ok: true };
      }),
      catchError(() => of({ ok: false })),
    );
  }

  /** null when the phase-count draft isn't dirty — see buildAlarmSaveRequest(). */
  private buildPhaseCountSaveRequest(
    deviceId: string,
  ): Observable<{ ok: boolean; detail?: string }> | null {
    if (!this.hasUnsavedPhaseCountChange()) return null;

    const phaseCount = this.phaseCountDraft();
    return this.deviceService.updatePhaseCount(deviceId, phaseCount).pipe(
      map(() => {
        this.savedPhaseCount.set(phaseCount);
        this.rawPhaseCount.set(phaseCount);
        this.deviceStore.setDevicePhaseCount(phaseCount);
        return { ok: true };
      }),
      catchError((error: { detail?: string }) => of({ ok: false, detail: error?.detail })),
    );
  }

  onResetDevice(): void {
    const deviceId = this.deviceDetail()?.internalDeviceStatus?.item.device_id;
    if (!deviceId) return;

    const dialogRef = this.dialog.open(ConfirmResetDeviceDialogComponent);
    dialogRef.afterClosed().subscribe(confirmed => {
      if (confirmed === true) {
        this.resetDevice(String(deviceId));
      }
    });
  }

  private resetDevice(deviceId: string): void {
    this.resetInProgress.set(true);
    this.statusService
      .resetDevice(deviceId)
      .pipe(
        catchError(() => of(null)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(result => {
        this.resetInProgress.set(false);
        this.snackBar.open(
          this.translateService.instant(
            result === null
              ? 'devices.alarms.settings.resetError'
              : 'devices.alarms.settings.resetSuccess',
          ),
          '',
          { horizontalPosition: 'right', verticalPosition: 'top', duration: 3000 },
        );
      });
  }
}
